/**
 * Le choix de la date et du créneau de livraison, dans le panier.
 *
 * ⚠️ POURQUOI UN CALENDRIER ET NON UNE LISTE
 * Trente-cinq dates dans un menu déroulant obligent à lire ligne à ligne pour
 * trouver un samedi. Une grille de mois se lit d'un coup d'œil, et un jour
 * fermé se voit au lieu d'être absent. C'est ce que fait tout site qui prend
 * des rendez-vous.
 *
 * ⚠️ POURQUOI L'HORLOGE DU NAVIGATEUR ET NON CELLE DU SERVEUR
 * Liquid ne peut pas calculer « maintenant + 24 heures » de façon fiable : la
 * page du panier est mise en cache, et une heure rendue au moment du cache
 * serait fausse dix minutes plus tard. L'horloge du client est la seule source
 * utilisable ici. Elle est manipulable, mais sans conséquence : la commande
 * passe sous les yeux de Vert Sapin avant d'être livrée.
 *
 * ⚠️ POURQUOI LES CRÉNEAUX NE SONT PAS UNE LISTE
 * Ils se déduisent de trois nombres posés dans les réglages de thème : la
 * première heure, la dernière, et la durée d'un créneau. Passer de 9 h - 21 h
 * à 8 h - 22 h, ou de créneaux d'une heure à des créneaux de trois, est un
 * déplacement de curseur, pas une réécriture.
 *
 * ⚠️ CE SCRIPT NE PORTE AUCUN TEXTE
 * Les noms de jours et de mois viennent d'Intl.DateTimeFormat dans la langue
 * de la page (l'attribut lang du document, posé par Liquid), et les libellés
 * arrivent par les attributs data-libelle-* du snippet, qui lit les locales.
 */

import { StandardEvents } from '@shopify/events';

const LANGUE = document.documentElement.lang || 'fr';
const formatDateComplete = new Intl.DateTimeFormat(LANGUE, { weekday: 'long', day: 'numeric', month: 'long' });
const formatMoisAnnee = new Intl.DateTimeFormat(LANGUE, { month: 'long', year: 'numeric' });

/** Deux chiffres, toujours : « 09 h » et non « 9 h ». */
const deuxChiffres = (n) => String(n).padStart(2, '0');

class LivraisonCreneau extends HTMLElement {
  connectedCallback() {
    this.ref = (nom) => this.querySelector(`[ref="${nom}"]`);

    /**
     * Le declencheur ou le panneau d'une paire, designe par son ROLE.
     *
     * ⚠️ POURQUOI PAS UN SIMPLE `ref`. Chaque paire est enveloppee dans un
     * `<anchored-popover-component>` du theme, qui exige les noms `trigger`
     * et `popover` -- et un element n'a qu'un attribut `ref`. Les deux paires
     * portent donc les MEMES noms, et un `querySelector` global rendrait
     * toujours la premiere. On passe par le role de la paire : `date` ou
     * `creneau`. Nomme, sans ambiguite, et independant de l'ordre du
     * balisage -- deplacer un panneau ne casse rien.
     */
    this.paire = (role, nom) => {
      const boite = this.querySelector(`anchored-popover-component[data-role="${role}"]`);
      if (!boite) throw new Error(`livraison-creneau : paire "${role}" absente du balisage`);
      const el = boite.querySelector(`[ref="${nom}"]`);
      if (!el) throw new Error(`livraison-creneau : "${nom}" absent de la paire "${role}"`);
      return el;
    };

    this.initialiser();
    this.surveillerLeRendu();
  }

  /**
   * Prend les references du balisage et cable tout ce qui en depend.
   *
   * ⚠️ POURQUOI CE N'EST PLUS DANS `connectedCallback`. Cette methode doit
   * pouvoir etre REJOUEE : voir `surveillerLeRendu()` plus bas.
   */
  initialiser() {
    // Panier sans sapin : aucun rendez-vous à prendre. On efface au passage un
    // choix laissé par un panier précédent, sinon une date de livraison
    // voyagerait avec une commande de décorations partie par la poste.
    if (this.dataset.avecSapin === 'non') {
      if (this.dataset.dateChoisie || this.dataset.creneauChoisi) this.effacer();
      // ⚠️ On oublie l'ancienne grille : sans cela `surveillerLeRendu()`
      // verrait un ecart a chaque mutation et rejouerait sans fin.
      this.grille = null;
      this.ecoutes?.abort();
      this.panierRendu = this.dataset.avecSapin;
      return;
    }

    this.dateValeur = this.ref('dateValeur');
    this.creneauValeur = this.ref('creneauValeur');
    this.creneauDeclencheur = this.paire('creneau', 'trigger');
    this.creneauPanneau = this.paire('creneau', 'popover');
    this.creneauLibelle = this.ref('creneauLibelle');
    this.creneauListe = this.ref('creneauListe');
    this.grille = this.ref('grille');
    this.moisLibelle = this.ref('moisLibelle');
    this.aide = this.ref('aide');
    if (!this.dateValeur || !this.creneauValeur || !this.grille) return;

    this.heurePremiere = Number(this.dataset.heurePremiere);
    this.heureDerniere = Number(this.dataset.heureDerniere);
    // ⚠️ Le repli à 1 n'est pas décoratif : une page de panier mise en cache
    // avant l'ajout de ce réglage ne porte pas l'attribut, et un pas de 0
    // ferait tourner la boucle des créneaux sans fin.
    this.dureeCreneau = Number(this.dataset.dureeCreneau) || 1;
    this.delai = Number(this.dataset.delai);

    // Le mois affiché au départ : celui de la date déjà choisie, sinon le
    // premier mois qui contient un jour livrable.
    const premier = this.premierJourLivrable();
    const choisie = this.dateDepuisCle(this.dataset.dateChoisie);
    const depart = choisie || premier;
    const defaut = depart ? new Date(depart.getFullYear(), depart.getMonth(), 1) : null;

    // ⚠️ ON GARDE LE MOIS QUE LE CLIENT FEUILLETAIT. `initialiser()` est
    // rejouee a chaque changement de panier : sans cette ligne, quelqu'un qui
    // consulte decembre et ajoute une guirlande serait ramene en novembre,
    // sans rien avoir demande. On ne conserve que si le mois tient encore dans
    // la saison, sinon on revient au defaut.
    const feuilletait = this.moisAffiche;
    const dansLaSaison =
      feuilletait &&
      premier &&
      feuilletait >= new Date(premier.getFullYear(), premier.getMonth(), 1) &&
      feuilletait <= new Date(this.saison.fin.getFullYear(), this.saison.fin.getMonth(), 1);
    this.moisAffiche = dansLaSaison ? feuilletait : defaut;

    this.declencheur = this.paire('date', 'trigger');
    this.panneau = this.paire('date', 'popover');
    this.dateLibelle = this.ref('dateLibelle');

    // ⚠️ AUCUN CALCUL DE POSITION ICI, ET C'EST LE POINT DE TOUT LE LOT.
    // Le panneau est ancre a son declencheur EN CSS (`position-anchor`), donc
    // par le navigateur. On ne mesure plus, on ne poursuit plus le
    // defilement. Voir le commentaire d'ancrage dans
    // `snippets/livraison-creneau.liquid` pour ce que couchait l'ancienne
    // version sur iOS.
    // ⚠️ UN SEUL INTERRUPTEUR POUR TOUTES LES ECOUTES, ET IL EST POSE ICI --
    // PAS PLUS HAUT. `initialiser()` est REJOUEE a chaque re-rendu (voir
    // `surveillerLeRendu()`). Selon ce que le re-alignement a fait, un noeud
    // peut avoir ete remplace -- son ecoute est partie avec lui -- ou CONSERVE
    // -- son ecoute est toujours la. Recabler sans precaution empilerait une
    // seconde ecoute sur les noeuds conserves, et un clic sur la fleche
    // sauterait alors DEUX mois. On coupe tout, on recable tout : le resultat
    // ne depend plus de ce qui a survecu. C'est le motif standard depuis que
    // `addEventListener` accepte un `signal`.
    //
    // ⚠️ POURQUOI SEULEMENT MAINTENANT. `paire()` LEVE une exception quand le
    // balisage lui manque. Couper les ecoutes avant d'avoir pris toutes les
    // references laisserait, en cas d'echec, un widget SANS AUCUNE ecoute :
    // ni fleches, ni panneaux, ni garde de paiement. A partir d'ici toutes les
    // references sont prises, plus rien ne peut echouer avant le recablage.
    this.ecoutes?.abort();
    this.ecoutes = new AbortController();
    const signal = this.ecoutes.signal;

    this.panneau.addEventListener('toggle', (e) => {
      this.declencheur.setAttribute(
        'aria-expanded',
        e.newState === 'open' ? 'true' : 'false'
      );
    }, { signal });

    this.creneauPanneau.addEventListener('toggle', (e) => {
      this.creneauDeclencheur.setAttribute(
        'aria-expanded',
        e.newState === 'open' ? 'true' : 'false'
      );
    }, { signal });

    this.ref('precedent').addEventListener('click', () => this.deplacerMois(-1), { signal });
    this.ref('suivant').addEventListener('click', () => this.deplacerMois(1), { signal });

    this.consigne = this.ref('consigne');
    this.garderLePaiement();

    // ⚠️ ON REVALIDE AVANT DE DESSINER. Un choix survit au panier : le client
    // peut revenir deux jours plus tard avec une date devenue impossible.
    this.revaliderLeChoix();

    this.dessiner();
    this.ecrireLibelle();
    this.remplirCreneaux();

    // Le message « choisissez une date » se rejoue depuis la memoire : le
    // re-alignement vient de le remasquer.
    this.appliquerConsigne();

    // ⚠️ EN DERNIER, ET C'EST VOULU. `surveillerLeRendu()` compare cet etat a
    // celui du balisage : ne le noter qu'apres un passage COMPLET garantit
    // qu'un echec en cours de route sera retente au prochain changement du
    // DOM, au lieu d'etre pris pour un travail fait.
    this.panierRendu = this.dataset.avecSapin;
  }

  /**
   * Efface un rendez-vous devenu impossible depuis qu'il a ete choisi.
   *
   * POURQUOI CE CONTROLE EXISTE (05/09/2026, decision de Quentin)
   * Une date n'est pas une preference, c'est un engagement date. Un panier
   * repris trois jours plus tard porte une date qui peut etre passee, sortie
   * de la saison, ou tombee sous le delai minimum. Sans revalidation, le
   * client lisait « vendredi 20 novembre » avec une liste de creneaux vide :
   * le garde bloquait bien le paiement, mais rien a l'ecran n'expliquait
   * pourquoi. On efface, il rechoisit, il comprend.
   *
   * ⚠️ Le creneau se nettoyait deja tout seul dans `remplirCreneaux` -- il
   * suffit qu'il ne soit plus dans la liste des ouverts. C'est la DATE qui
   * restait affichee. On traite donc les deux au meme endroit, sinon la
   * regle vit a deux endroits qui peuvent diverger.
   */
  revaliderLeChoix() {
    const valeur = this.dateValeur.value;
    if (!valeur) return;

    const jour = this.dateDepuisCle(valeur);
    // `jourLivrable` teste deja qu il reste au moins un creneau ce jour-la.
    if (jour && this.jourLivrable(jour)) return;

    this.dateValeur.value = '';
    this.creneauValeur.value = '';
    this.dataset.dateChoisie = '';
    this.moisAffiche = (() => {
      const p = this.premierJourLivrable();
      return p ? new Date(p.getFullYear(), p.getMonth(), 1) : null;
    })();
    this.effacer();
  }

  /**
   * Empêche de passer au paiement tant que la date et le créneau ne sont pas
   * choisis — mais SEULEMENT si le panier contient un sapin. Une commande de
   * décorations part par la poste et n'a pas de rendez-vous à prendre : ce
   * garde ne s'installe donc jamais dans ce cas, puisqu'on est déjà sorti de
   * `connectedCallback` plus haut.
   *
   * ⚠️ CE GARDE EST CÔTÉ CLIENT, ET IL NE PEUT PAS ÊTRE AUTRE CHOSE.
   * La réponse robuste de Shopify est une fonction de validation de panier,
   * qui tourne sur ses serveurs et qu'un client ne peut pas contourner. Elle
   * demande une app personnalisée, réservée au plan Plus (doc Shopify) — la
   * boutique est en Basic. Ce qui rend ce garde efficace malgré tout, c'est
   * que le panier n'a plus qu'UN chemin vers le paiement : les boutons de
   * paiement accéléré ont été retirés du panier le 04/09/2026, justement
   * parce qu'ils sautaient par-dessus. Reste l'URL /checkout tapée à la main,
   * que personne ne tape.
   *
   * ⚠️ ON NE DÉSACTIVE PAS LE BOUTON. Un bouton grisé ne dit pas POURQUOI, et
   * un lecteur d'écran passe dessus sans le lire. On laisse cliquer, on
   * empêche l'envoi, et on explique.
   */
  garderLePaiement() {
    const formulaire = document.getElementById('cart-form');
    if (!formulaire || !this.consigne) return;

    // Le panier se re-rend à chaque changement : l'élément se reconnecte et
    // repasserait ici. Un AbortController remplace proprement l'écoute
    // précédente au lieu d'en empiler une seconde.
    if (this.arret) this.arret.abort();
    this.arret = new AbortController();

    formulaire.addEventListener(
      'submit',
      (e) => {
        // ⚠️ UN CHOIX OBLIGATOIRE DONT L'ENSEMBLE DES OPTIONS EST VIDE N'EST
        // PAS UNE REGLE, C'EST UN VERROU. Quand plus aucun creneau n'est
        // proposable, le rendez-vous se prend par telephone : les deux champs
        // partent avec la mention convenue -- pour que la commande DISE
        // pourquoi elle n'a pas de date -- et le paiement passe. Le garde
        // reste entier partout ailleurs. → [D51] [D18]
        if (this.aucunCreneauProposable) {
          const aConvenir = this.dataset.libelleAConvenir;
          this.dateValeur.value = aConvenir;
          this.creneauValeur.value = aConvenir;
          return;
        }
        if (this.rendezVousPris()) {
          this.appliquerConsigne();
          return;
        }
        e.preventDefault();
        this.signalerManque();
      },
      { capture: true, signal: this.arret.signal }
    );

    // ⚠️ PAS d'écouteur `change` sur les champs cachés : le script pose leur
    // valeur par code, ce qui n'émet aucun événement. Une première version
    // écoutait `change` et le message restait affiché après correction —
    // mesuré le 04/09/2026. C'est `enregistrer()`, appelé à chaque choix,
    // qui rafraîchit le message.
  }

  rendezVousPris() {
    return Boolean(this.dateValeur.value && this.creneauValeur.value);
  }


  /**
   * Ecrit la consigne a l'ecran, DERIVEE de l'etat du panier.
   *
   * ⚠️ AUCUN SOUVENIR, ET C'EST TOUT LE POINT. La version d'avant repondait
   * a « a-t-il deja clique Payer ? » -- un EVENEMENT, donc quelque chose a
   * retenir. Celle-ci repond a « y a-t-il un rendez-vous ? » -- un ETAT,
   * qu'on lit dans le panier. Il n'y a donc plus rien a garder, ni dans un
   * attribut du balisage, ni en memoire d'instance, ni en `sessionStorage` :
   * les trois ont ete essayes dans la meme journee, le 06/09/2026, et les
   * deux premiers ont echoue -- l'un au re-rendu du tiroir, l'autre au
   * changement de page. Meme bascule que D40. La version remplacee, qui
   * marchait, est au `docs/REMPLACEES.md`.
   *
   * ⚠️ LE SERVEUR DECIDE DEJA, dans `snippets/livraison-creneau.liquid` : il
   * pose `hidden` quand la date ET le creneau sont au panier. Cette methode
   * ne fait que suivre entre deux rendus, quand le client choisit.
   *
   * ⚠️ ELLE N'OUVRE RIEN ET NE FAIT PAS DEFILER. Le geste bruyant reste dans
   * `signalerManque()`, appele seulement sur un vrai clic « Payer ».
   */
  appliquerConsigne() {
    if (!this.consigne || !this.declencheur || !this.creneauDeclencheur) return;

    // Rien a reclamer quand rien n'est proposable : le message de
    // `dessiner()` dit deja la verite, et marquer les champs en rouge
    // designerait une faute que le client n'a pas commise. → [D51]
    if (this.aucunCreneauProposable) {
      this.consigne.hidden = true;
      this.declencheur.classList.remove('livraison-creneau__declencheur--manque');
      this.creneauDeclencheur.classList.remove('livraison-creneau__declencheur--manque');
      return;
    }

    const manque = !this.rendezVousPris();
    this.consigne.hidden = !manque;
    this.declencheur.classList.toggle(
      'livraison-creneau__declencheur--manque',
      manque && !this.dateValeur.value
    );
    this.creneauDeclencheur.classList.toggle(
      'livraison-creneau__declencheur--manque',
      manque && Boolean(this.dateValeur.value) && !this.creneauValeur.value
    );
  }



  /**
   * Montre le message, marque le champ qui manque, et ouvre le bon panneau —
   * dire « il manque quelque chose » sans montrer OÙ ne sert à rien.
   */
  signalerManque() {
    this.appliquerConsigne();

    const manqueDate = !this.dateValeur.value;
    const cible = manqueDate ? this.declencheur : this.creneauDeclencheur;

    this.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // On ouvre le panneau du champ vide. Le créneau reste désactivé tant
    // qu'aucune date n'est choisie : dans ce cas c'est le calendrier qu'il
    // faut ouvrir, jamais la liste des heures.
    if (cible.showPopover && !cible.disabled) {
      try {
        cible.click();
      } catch (e) {
        // Un navigateur qui refuse l'ouverture laisse le message visible :
        // le client voit quand même ce qui manque.
      }
    }
    cible.focus({ preventScroll: true });
  }



  /** Le déclencheur porte la date choisie : replié, il reste informatif. */
  ecrireLibelle() {
    const d = this.dateDepuisCle(this.dateValeur.value);
    this.dateLibelle.textContent = d
      ? formatDateComplete.format(d)
      : this.dataset.libelleChoisirDate;
  }

  /** Remet le rendez-vous à zéro dans le panier. */
  async effacer() {
    try {
      const url = (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update';
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Date de livraison': '', 'Créneau de livraison': '' } }),
      });
    } catch (e) {
      // Sans réseau, le choix restera : il sera écarté au traitement de la commande.
    }
  }

  /** Le premier instant livrable : maintenant plus le délai minimum. */
  get plancher() {
    const t = new Date();
    t.setHours(t.getHours() + this.delai);
    return t;
  }

  /**
   * Les bornes de la saison. Si celle de cette année est passée, on bascule
   * sur la suivante — sans quoi la boutique n'afficherait plus aucune date
   * dès le 25 décembre.
   */
  get saison() {
    const jd = Number(this.dataset.jourDebut);
    const md = Number(this.dataset.moisDebut) - 1;
    const jf = Number(this.dataset.jourFin);
    const mf = Number(this.dataset.moisFin) - 1;

    let annee = new Date().getFullYear();
    let fin = new Date(annee, mf, jf, 23, 59, 59);
    if (this.plancher > fin) {
      annee += 1;
      fin = new Date(annee, mf, jf, 23, 59, 59);
    }
    return { debut: new Date(annee, md, jd, 0, 0, 0), fin };
  }

  /** Une date en `AAAA-MM-JJ`, sans passer par UTC qui décalerait d'un jour. */
  static cle(d) {
    return `${d.getFullYear()}-${deuxChiffres(d.getMonth() + 1)}-${deuxChiffres(d.getDate())}`;
  }

  dateDepuisCle(cle) {
    if (!cle) return null;
    const [a, m, j] = cle.split('-').map(Number);
    if (!a || !m || !j) return null;
    return new Date(a, m - 1, j);
  }

  /**
   * Les créneaux encore ouverts pour un jour donné. Sur le premier jour
   * livrable, ceux qui tombent avant le délai minimum sont écartés : sans
   * quoi on proposerait une livraison dans deux heures.
   */
  creneauxDuJour(jour) {
    const plancher = this.plancher;
    const duree = this.dureeCreneau;
    const ouverts = [];
    /**
     * ⚠️ LA CONDITION PORTE SUR LA FIN DU CRÉNEAU, PAS SUR SON DÉBUT.
     * Avec `h < heureDerniere`, une durée de 4 h dans une fenêtre 9 h - 20 h
     * proposerait un créneau 19 h - 23 h, deux heures après la fermeture.
     * `h + duree <= heureDerniere` garantit qu'aucun créneau ne déborde.
     * Pour une durée d'une heure, les deux conditions sont équivalentes :
     * le comportement d'origine est intact.
     *
     * ⚠️ Une fenêtre que la durée ne divise pas laisse un reste inutilisé —
     * 9 h - 20 h par pas de 4 h donne 9-13 et 13-17, et laisse 17-20 de côté.
     * C'est voulu : mieux vaut un créneau en moins qu'un créneau qu'on ne
     * peut pas tenir. Le réglage le dit dans son texte d'aide.
     */
    for (let h = this.heurePremiere; h + duree <= this.heureDerniere; h += duree) {
      const debut = new Date(jour.getFullYear(), jour.getMonth(), jour.getDate(), h, 0, 0);
      if (debut < plancher) continue;
      ouverts.push(
        this.dataset.formatCreneau
          .replace('__DEBUT__', deuxChiffres(h))
          .replace('__FIN__', deuxChiffres(h + duree)),
      );
    }
    return ouverts;
  }

  /** Un jour est livrable s'il est dans la saison et lui reste un créneau. */
  jourLivrable(d) {
    const { debut, fin } = this.saison;
    if (d < new Date(debut.getFullYear(), debut.getMonth(), debut.getDate())) return false;
    if (d > fin) return false;
    return this.creneauxDuJour(d).length > 0;
  }

  premierJourLivrable() {
    const { debut, fin } = this.saison;
    const d = new Date(Math.max(debut.getTime(), this.plancher.getTime()));
    d.setHours(0, 0, 0, 0);
    for (; d <= fin; d.setDate(d.getDate() + 1)) {
      if (this.jourLivrable(d)) return new Date(d);
    }
    return null;
  }

  /**
   * Reprend la main chaque fois que le serveur a repasse sur notre balisage.
   *
   * ⚠️ L'ORIGINE DU DEFAUT, ET ELLE EST ARCHITECTURALE.
   * Le serveur rend nos deux conteneurs VIDES : la grille des jours et la
   * liste des creneaux n'existent que parce que ce script les fabrique. Or a
   * chaque changement de panier, Shopify re-rend la section du tiroir et
   * `morph.js` REALIGNE l'arbre vivant sur la version du serveur. Notre
   * contenu, que le serveur ignore, est donc detruit -- par construction, pas
   * par accident.
   *
   * ⚠️ NOUS SOMMES LE SEUL COMPOSANT DU TIROIR DANS CE CAS. Tous les autres
   * survivent parce que LEUR contenu vient du serveur : il n'y a rien a
   * perdre. Le notre est le seul a inventer du DOM que le serveur ne connait
   * pas. C'est la vraie cause, et elle explique tous les symptomes vus le
   * 05 et le 06/09/2026.
   *
   * ⚠️ UN SEUL INVARIANT, PAS UNE LISTE DE CAS -- c'est tout l'objet de cette
   * methode. On a d'abord traite les symptomes un par un, et il en restait
   * toujours un :
   *     grille VIDEE, noeud conserve   (ajouter une decoration a un panier
   *                                     qui a deja un sapin)
   *     grille REMPLACEE, noeud detache (retirer puis remettre le sapin en
   *                                     gardant une decoration au panier)
   * Or quoi que fasse le realignement -- vider, remplacer, deplacer -- la
   * consequence observable est TOUJOURS la meme : ce qui devait etre dessine
   * ne l'est plus. On ne compare donc pas des noeuds, on compare l'ETAT
   * DEMANDE par le balisage a l'ETAT REELLEMENT DESSINE. Il ne peut pas
   * exister de troisieme cas : il n'y a rien que le realignement puisse faire
   * a notre sous-arbre qui laisse la grille correctement remplie.
   *
   * ⚠️ AUCUNE BOUCLE POSSIBLE. Redessiner rend les deux etats egaux, donc le
   * rappel suivant ne fait rien. Et le cas « saison finie » -- ou il est
   * NORMAL de n'avoir aucun jour -- est couvert par `moisAffiche`, qui vaut
   * alors `null` : on n'attend aucun jour, on n'en voit aucun, les deux etats
   * concordent.
   *
   * ⚠️ POURQUOI PAS `data-skip-subtree-update`, LE VRAI OUTIL NATIF.
   * Il existe (`morph.js:571-579`), il protege bien le CONTENU d'un noeud, et
   * le theme s'en sert exactement pour ca dans `quick-order-list.liquid:393`.
   * On l'a ecarte pour une raison mesuree : il ne protegerait que la grille et
   * la liste des creneaux, alors que le re-rendu detruit une DOUZAINE d'etats
   * clients qui vivent AILLEURS -- le libelle de la date sur le declencheur,
   * celui du creneau, le libelle du mois, le texte d'aide, `aria-expanded`,
   * `aria-pressed`, le `disabled` des deux fleches, et surtout le `disabled`
   * du champ creneau, qui est ecrit EN DUR dans le balisage serveur et que
   * seul le JS retire. Etendre l'attribut a tout le widget figerait aussi les
   * changements que le serveur DOIT faire passer (`data-avec-sapin`).
   * Rejouer l'initialisation restaure les douze d'un coup : mesure du
   * 06/09/2026, dix etats observes identiques avant et apres l'ajout d'une
   * decoration, rendez-vous deja choisi.
   *
   * ⚠️ TROIS PISTES ESSAYEES ET MESUREES FAUSSES, pour qu'on ne les reprenne
   * pas :
   *   - `data-skip-node-update` sur la grille -- a ne pas confondre avec
   *     l'attribut ci-dessus : `morph.js` dit « don't morph this node, but
   *     recurse into its children ». Il protege les ATTRIBUTS d'un noeud,
   *     jamais son CONTENU. Pose des deux cotes, grille toujours videe.
   *   - redessiner sur `shopify:cart:lines-update` : l'evenement part bien,
   *     deux fois, mais le realignement passe APRES et efface le redessin,
   *     meme en reessayant jusqu'a 2,7 secondes.
   *   - heriter de `Component` pour recevoir `updatedCallback()` : le crochet
   *     EXISTE (`morph.js:91`) mais NE PART JAMAIS. Mesure le 06/09/2026 :
   *     zero appel pendant une mise a jour de panier, y compris pour les
   *     composants natifs du theme. `morph.js:260` passe le noeud issu du
   *     `DOMParser`, qui n'est pas mis a niveau -- `instanceof Component` y
   *     est donc toujours faux.
   */
  surveillerLeRendu() {
    if (this.observateur) return;
    this.observateur = new MutationObserver(() => {
      // Ce que le balisage DEMANDE : des jours, sauf si le panier n'a pas de
      // sapin ou si la saison ne propose plus rien.
      const demande = this.dataset.avecSapin !== 'non' && this.moisAffiche != null;
      // Ce qui est REELLEMENT dessine, dans le document vivant.
      const dessine = Boolean(this.grille?.isConnected && this.grille.children.length);
      // Et l'etat de panier pour lequel on a travaille la derniere fois.
      //
      // ⚠️ CETTE TROISIEME COMPARAISON N'EST PAS UN CAS DE PLUS, c'est le
      // meme invariant applique a l'autre chose que le serveur nous dit :
      // l'attribut `data-avec-sapin`. Sans elle, retirer le sapin d'un panier
      // qui GARDE une decoration ne reveille personne -- rien n'est dessine
      // avant, rien apres -- et le rendez-vous resterait accroche au panier.
      // Une date de livraison partirait avec une commande de decorations
      // expediee par la poste. Mesure le 06/09/2026.
      const memePanier = this.dataset.avecSapin === this.panierRendu;
      if (demande !== dessine || !memePanier) this.initialiser();
    });
    this.observateur.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-avec-sapin'],
    });
  }

  deplacerMois(pas) {
    if (!this.moisAffiche) return;
    this.moisAffiche = new Date(this.moisAffiche.getFullYear(), this.moisAffiche.getMonth() + pas, 1);
    this.dessiner();
  }

  dessiner() {
    const { fin } = this.saison;
    const premier = this.premierJourLivrable();

    /**
     * ⚠️ AUCUN CRENEAU PROPOSABLE — ET CE N'EST PAS « HORS SAISON ».
     * On arrive ici quand la saison COURT ENCORE mais que le delai minimum
     * passe au-dela de son dernier creneau. Mesure du 07/09/2026 : le 23/12
     * a partir de 20 h 00, le plancher de 24 h tombe apres le dernier
     * creneau du 24/12 (il commence a 20 h, `heure_derniere` etant une FIN),
     * et la saison ne bascule sur l'annee suivante qu'a 23 h 59 min 59 s --
     * une fenetre de QUATRE HEURES, le soir le plus charge de l'annee.
     *
     * ⚠️ C'est le SEUL chemin qui mene ici. Avant la saison,
     * `premierJourLivrable()` rend le premier jour ouvert, jamais `null` :
     * l'ancien libelle « les livraisons reprennent le {date} » ne s'affichait
     * donc QUE dans cette fenetre, ou il etait faux deux fois -- la date
     * annoncee etait passee, et le garde bloquait le paiement pendant qu'il
     * promettait « vous pouvez commander des maintenant ». → [D51]
     */
    this.aucunCreneauProposable = !premier || !this.moisAffiche;

    if (this.aucunCreneauProposable) {
      this.grille.textContent = '';
      this.moisLibelle.textContent = '';
      this.creneauDeclencheur.disabled = true;
      this.aide.textContent = this.dataset.libelleSansCreneau;
      // ⛔ Pas de consigne « choisissez une date » : il n'y a rien a choisir.
      if (this.consigne) this.consigne.hidden = true;
      return;
    }

    const annee = this.moisAffiche.getFullYear();
    const mois = this.moisAffiche.getMonth();
    this.moisLibelle.textContent = formatMoisAnnee.format(this.moisAffiche);

    // Les flèches s'éteignent aux bornes de la saison : on ne feuillette pas
    // vers des mois qui n'ont rien à proposer.
    this.ref('precedent').disabled = new Date(annee, mois, 1) <= new Date(premier.getFullYear(), premier.getMonth(), 1);
    this.ref('suivant').disabled = new Date(annee, mois, 1) >= new Date(fin.getFullYear(), fin.getMonth(), 1);

    this.grille.textContent = '';

    // La semaine commence le lundi : getDay() rend 0 pour dimanche.
    const premierDuMois = new Date(annee, mois, 1);
    const decalage = (premierDuMois.getDay() + 6) % 7;
    for (let i = 0; i < decalage; i += 1) {
      const vide = document.createElement('span');
      vide.className = 'livraison-calendrier__vide';
      this.grille.append(vide);
    }

    const nbJours = new Date(annee, mois + 1, 0).getDate();
    const choisie = this.dateValeur.value;
    for (let j = 1; j <= nbJours; j += 1) {
      const d = new Date(annee, mois, j);
      const cle = LivraisonCreneau.cle(d);
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'livraison-calendrier__jour';
      b.textContent = String(j);
      b.dataset.cle = cle;
      if (!this.jourLivrable(d)) {
        b.disabled = true;
      } else {
        b.setAttribute('aria-pressed', cle === choisie ? 'true' : 'false');
        b.addEventListener('click', () => this.choisir(cle));
      }
      this.grille.append(b);
    }
  }

  choisir(cle) {
    this.dateValeur.value = cle;
    this.dataset.dateChoisie = cle;
    for (const b of this.grille.querySelectorAll('[aria-pressed]')) {
      b.setAttribute('aria-pressed', b.dataset.cle === cle ? 'true' : 'false');
    }
    this.ecrireLibelle();
    // Le calendrier se referme dès que la date est prise : le client n'a pas à
    // le replier lui-même, et le créneau devient le point d'attention suivant.
    if (this.panneau && this.panneau.hidePopover) this.panneau.hidePopover();
    this.remplirCreneaux();
    this.enregistrer();
  }

  remplirCreneaux() {
    // ⚠️ NE RIEN EFFACER QUAND RIEN N'EST PROPOSABLE. `initialiser()` appelle
    // cette methode JUSTE APRES `dessiner()`, et sa branche « pas encore de
    // date » remet l'aide a vide : le message qui explique l'absence de
    // creneau disparaissait aussitot ecrit. Mesure du 07/09/2026, sur un
    // theme de developpement ou la fenetre etait reproduite. → [D51]
    if (this.aucunCreneauProposable) return;

    const valeur = this.dateValeur.value;
    this.creneauListe.textContent = '';

    if (!valeur) {
      this.creneauDeclencheur.disabled = true;
      this.creneauLibelle.textContent = this.dataset.libelleDabordUneDate;
      this.aide.textContent = '';
      return;
    }

    const ouverts = this.creneauxDuJour(this.dateDepuisCle(valeur));
    // Un créneau retenu la veille peut ne plus être ouvert aujourd'hui.
    if (!ouverts.includes(this.creneauValeur.value)) this.creneauValeur.value = '';

    this.creneauDeclencheur.disabled = false;
    for (const c of ouverts) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'livraison-creneaux__choix';
      b.setAttribute('role', 'option');
      b.textContent = c;
      b.setAttribute('aria-selected', c === this.creneauValeur.value ? 'true' : 'false');
      b.addEventListener('click', () => this.choisirCreneau(c));
      this.creneauListe.append(b);
    }
    this.ecrireLibelleCreneau();
    this.aide.textContent = '';
  }

  ecrireLibelleCreneau() {
    this.creneauLibelle.textContent = this.creneauValeur.value || this.dataset.libelleChoisirCreneau;
  }

  choisirCreneau(c) {
    this.creneauValeur.value = c;
    for (const b of this.creneauListe.querySelectorAll('[role="option"]')) {
      b.setAttribute('aria-selected', b.textContent === c ? 'true' : 'false');
    }
    this.ecrireLibelleCreneau();
    if (this.creneauPanneau.hidePopover) this.creneauPanneau.hidePopover();
    this.enregistrer();
  }

  /**
   * On enregistre à chaque changement plutôt qu'au seul clic sur « Payer » :
   * le client peut recharger sa page ou revenir plus tard sans reperdre son
   * choix. Les champs restent rattachés au formulaire du panier, ce qui garde
   * le fonctionnement même si ce script ne se charge pas.
   */
  async enregistrer() {
    this.appliquerConsigne();

    const attributes = {
      'Date de livraison': this.dateValeur.value || '',
      'Créneau de livraison': this.creneauValeur.value || '',
    };

    try {
      const url = (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update';
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (e) {
      // Sans réseau, le formulaire du panier transmettra quand même le choix
      // au moment du paiement : il n'y a rien à rattraper ici.
    }
  }
}

if (!customElements.get('livraison-creneau')) {
  customElements.define('livraison-creneau', LivraisonCreneau);
}

/**
 * ⚠️⚠️ LE NETTOYAGE VIT ICI, AU NIVEAU DU MODULE, ET PAS DANS L'ELEMENT.
 *
 * Signale trois fois par Quentin avant qu'on trouve la vraie cause. Le
 * parcours : un sapin au panier, une date choisie, on SUPPRIME le sapin, on
 * en rajoute un — la date d'avant etait toujours la.
 *
 * MESURE DU 05/09/2026, dans le tiroir, sans rechargement : quand le dernier
 * sapin part, le panier devient vide, le tiroir bascule sur son balisage
 * « panier vide » et `<livraison-creneau>` DISPARAIT DU DOM. Le composant qui
 * sait nettoyer n'existe plus au moment ou il faudrait nettoyer.
 *
 * Toutes les tentatives precedentes echouaient pour la meme raison de fond :
 * elles vivaient dans du balisage conditionne au panier — or c'est
 * exactement ce balisage-la qui dispararait. Un rechargement les faisait
 * passer, ce qui rendait le defaut trompeur a tester.
 *
 * ⚠️ LE MODULE, LUI, RESTE CHARGE. Il est appele par `cart-drawer.liquid`,
 * hors de toute condition sur le panier. C'est donc le seul endroit stable
 * ou accrocher le nettoyage.
 *
 * On ecoute `StandardEvents.cartLinesUpdate` — l'evenement STANDARD de
 * Shopify, que le theme ecoute deja dans `cart-icon`, `cart-drawer` et
 * `component-cart-items`. On ne fabrique pas de signal maison.
 *
 * ⚠️ AUCUNE REGLE DUPLIQUEE. On ne redemande pas « ce panier contient-il un
 * sapin ? » : la presence du widget EST la reponse rendue par Liquid. Absent,
 * ou present en `data-avec-sapin="non"` — dans les deux cas, aucun
 * rendez-vous n'a lieu d'etre.
 */
/**
 * ⚠️ L'EVENEMENT DE PANIER EST OPTIMISTE, ET C'EST TOUT LE PIEGE.
 *
 * Mesure du 05/09/2026 : au moment ou `shopify:cart:lines-update` part,
 * `/cart.js` repond encore `item_count: 1` pour un panier qu'on vient de
 * vider. L'evenement ne porte AUCUN detail non plus (verifie : `e.detail`
 * est vide). Une version precedente lisait donc le panier a cet instant et
 * sortait toujours par son garde -- le nettoyage ne partait jamais.
 *
 * On relit donc quelques fois, sur environ deux secondes.
 *
 * ⚠️ CETTE ATTENTE EST SANS RISQUE, et c'est ce qui la rend acceptable :
 * on n'efface QUE si le panier est vide, et un panier vide ne peut jamais
 * porter legitimement un rendez-vous. Le pire cas n'est pas un effacement
 * a tort — c'est un effacement manque, rattrape au prochain evenement de
 * panier ou au prochain rendu de page.
 */
const RELECTURES_MS = [0, 300, 800, 1600];

/**
 * Rend `true` quand il n'y a plus rien a faire : soit on a efface, soit il
 * n'y avait rien a effacer. Rend `false` tant que le panier n'est pas encore
 * vide — auquel cas on relira.
 */
async function nettoyerSiPanierVide() {
  const panier = await fetch(`${Shopify.routes.root}cart.js`).then((r) => r.json());
  const attributs = panier.attributes || {};
  const aUnRendezVous = attributs['Date de livraison'] || attributs['Créneau de livraison'];

  // Rien a effacer. On ne poste pas : l'ecriture declencherait un nouvel
  // evenement de panier, et la boucle tournerait sans fin.
  if (!aUnRendezVous) return true;

  // Panier encore garni : soit il reste un sapin — et le rendez-vous est
  // legitime —, soit le serveur n'a pas fini. On relira.
  if (panier.item_count > 0) return false;

  await fetch(`${Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { 'Date de livraison': '', 'Créneau de livraison': '' } }),
  });
  return true;
}

document.addEventListener(StandardEvents.cartLinesUpdate, async () => {
  try {
    for (const attente of RELECTURES_MS) {
      if (attente) await new Promise((r) => setTimeout(r, attente));
      if (await nettoyerSiPanierVide()) return;
    }
  } catch (e) {
    // Sans reseau, le choix restera : il sera ecarte au traitement de la commande.
  }
});
